import React from "react";
import {
  Paper,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Skeleton,
} from "@mui/material";
import { AvailableYearsAndWeeksResponse } from "../types/score";

interface ScoreboardHeaderProps {
  viewMode: "year" | "week";
  selectedYear: number;
  selectedWeek: number;
  availableYearsAndWeeks: AvailableYearsAndWeeksResponse;
  lastUpdated: Date | null;
  isMobile: boolean;
  loading?: boolean;
  onViewChange: (
    event: React.MouseEvent<HTMLElement>,
    newView: "year" | "week" | null
  ) => void;
  onYearChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onWeekChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onRefresh: () => void;
}

const ScoreboardHeader: React.FC<ScoreboardHeaderProps> = ({
  viewMode,
  selectedYear,
  selectedWeek,
  availableYearsAndWeeks,
  lastUpdated,
  isMobile,
  loading = false,
  onViewChange,
  onYearChange,
  onWeekChange,
  onRefresh,
}) => {
  if (loading) {
    return <ScoreboardHeaderSkeleton isMobile={isMobile} />;
  }
  return (
    <Paper elevation={2}>
      <Box
        sx={{
          background: "linear-gradient(135deg, #1976d2, #42a5f5)",
          color: "white",
          padding: isMobile ? 1.5 : 2,
        }}
      >
        <Typography
          variant={isMobile ? "h6" : "h5"}
          sx={{ fontWeight: 600, marginBottom: 1 }}
        >
          Scoreboard
        </Typography>
        {viewMode === "week" && lastUpdated && (
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            Last updated: {lastUpdated.toLocaleTimeString()}
          </Typography>
        )}

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={onViewChange}
          size={isMobile ? "small" : "medium"}
          sx={{
            "& .MuiToggleButton-root": {
              color: "white",
              borderColor: "rgba(255,255,255,0.3)",
              "&.Mui-selected": {
                backgroundColor: "rgba(255,255,255,0.2)",
                color: "white",
              },
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.1)",
              },
            },
          }}
        >
          <ToggleButton value="year">Year View</ToggleButton>
          <ToggleButton value="week">Week View</ToggleButton>
        </ToggleButtonGroup>

        {/* Year / Week selectors */}
        {Object.keys(availableYearsAndWeeks.availableScores).length > 0 && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              marginTop: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body2"
                sx={{ color: "white", fontWeight: 500 }}
              >
                Year:
              </Typography>
              <select
                value={selectedYear}
                onChange={onYearChange}
                style={{
                  padding: "4px 8px",
                  borderRadius: "4px",
                  border: "1px solid rgba(255,255,255,0.3)",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  color: "white",
                  fontSize: "0.875rem",
                }}
              >
                {Object.keys(availableYearsAndWeeks.availableScores)
                  .map(Number)
                  .sort((a, b) => b - a)
                  .map((year) => (
                    <option
                      key={year}
                      value={year}
                      style={{
                        backgroundColor: "#333",
                        color: "white",
                      }}
                    >
                      {year}
                    </option>
                  ))}
              </select>
            </Box>

            {viewMode === "week" && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "white", fontWeight: 500 }}
                >
                  Week:
                </Typography>
                <select
                  value={selectedWeek}
                  onChange={onWeekChange}
                  style={{
                    padding: "4px 8px",
                    borderRadius: "4px",
                    border: "1px solid rgba(255,255,255,0.3)",
                    backgroundColor: "rgba(255,255,255,0.1)",
                    color: "white",
                    fontSize: "0.875rem",
                  }}
                >
                  {availableYearsAndWeeks.availableScores[selectedYear]?.map(
                    (week) => (
                      <option
                        key={week}
                        value={week}
                        style={{
                          backgroundColor: "#333",
                          color: "white",
                        }}
                      >
                        {week}
                      </option>
                    )
                  )}
                </select>
                <Chip
                  label="Refresh"
                  size="small"
                  onClick={onRefresh}
                  sx={{
                    ml: 1,
                    backgroundColor: "rgba(255,255,255,0.2)",
                    color: "white",
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

// Skeleton component for loading state
export const ScoreboardHeaderSkeleton: React.FC<{ isMobile?: boolean }> = ({
  isMobile = false,
}) => {
  const mobilePadding = isMobile ? 1.5 : 2;

  return (
    <Paper elevation={2}>
      <Box
        sx={{
          background: "linear-gradient(135deg, #1976d2, #42a5f5)",
          color: "white",
          padding: mobilePadding,
        }}
      >
        <Skeleton
          variant="text"
          width="40%"
          height={isMobile ? 24 : 32}
          sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
        />
        <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
          <Skeleton
            variant="rectangular"
            width={100}
            height={32}
            sx={{ bgcolor: "rgba(255,255,255,0.3)", borderRadius: 1 }}
          />
          <Skeleton
            variant="rectangular"
            width={100}
            height={32}
            sx={{ bgcolor: "rgba(255,255,255,0.3)", borderRadius: 1 }}
          />
        </Box>
        <Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Skeleton
            variant="rectangular"
            width={80}
            height={32}
            sx={{ bgcolor: "rgba(255,255,255,0.3)", borderRadius: 1 }}
          />
          <Skeleton
            variant="rectangular"
            width={80}
            height={32}
            sx={{ bgcolor: "rgba(255,255,255,0.3)", borderRadius: 1 }}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default ScoreboardHeader;
